import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { X, Loader2, AlertTriangle, Check, ExternalLink, Sparkles, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';
import { Button } from '../ui/button';

// Import refactored sub-components
import {
  AIBriefSection,
  FullAnalysisModal,
  ReplyPanel,
  ArchiveModal,
  CaseSelectorModal,
  CreateCaseModal
} from './triage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * INBOX TRIAGE PANEL V2 - Refactored
 * 
 * Design Philosophy:
 * - ONE decision: Choose case routing before anything else
 * - NO competing CTAs during triage
 * - Progressive disclosure: Show only what's needed
 * 
 * Structure:
 * A) AI Intake Brief (compact top + expandable)
 * B) Triage Choices (5 outcomes, clear hierarchy)
 * C) Context info AFTER choice (not before)
 */
const InboxTriagePanelV2 = ({ 
  email, 
  isOpen, 
  onClose, 
  onAssignCase,
  onNavigateToCase,
  cases = [],
  width = 400 
}) => {
  const { t } = useTranslation(['inbox', 'common']);
  
  // State
  const [triageData, setTriageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCaseSelector, setShowCaseSelector] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  
  // Secondary action states
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  
  // Create case modal state
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  
  // Full analysis modal state
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  
  // Fetch triage data
  const fetchTriageData = useCallback(async () => {
    if (!email?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = auth.getToken();
      
      // For Gmail emails (source: 'google'), use the Gmail triage endpoint
      if (email.source === 'google') {
        try {
          const response = await axios.get(
            `${API}/google/mail/messages/${email.id}/triage`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setTriageData(response.data);
        } catch (gmailErr) {
          console.warn('Gmail triage error, using fallback:', gmailErr);
          // Fallback to basic info
          const senderName = email.from?.name || email.senderName || email.sender || 'Ukjent';
          const senderEmail = email.from?.email || email.sender || '';
          
          setTriageData({
            message_id: email.id,
            status: 'NEW',
            current_case_id: null,
            ai_brief: {
              category: 'E-post',
              priority: 'NORMAL',
              risk_level: 'LOW',
              sender_name: senderName,
              sender_email: senderEmail,
              sender_org: null,
              what_this_is: email.body_preview || email.body || email.snippet || email.subject || null,
              known_facts: [],
              missing_info: [],
              red_flags: [],
              yellow_flags: []
            },
            attachments: email.attachments || [],
            source: 'google'
          });
        }
      } else {
        // For Outlook emails, use the standard triage endpoint
        const response = await axios.get(
          `${API}/inbox/triage/email/${email.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTriageData(response.data);
      }
    } catch (err) {
      console.error('Error fetching triage data:', err);
      setError('Kunne ikke laste triage-data');
      // Fall back to email's existing ai_analysis
      if (email.ai_analysis) {
        setTriageData({
          message_id: email.id,
          status: email.case_id ? 'ASSIGNED' : 'NEW',
          current_case_id: email.case_id,
          ai_brief: transformLegacyAnalysis(email.ai_analysis),
          attachments: email.attachments || []
        });
      }
    } finally {
      setLoading(false);
    }
  }, [email]);
  
  // Re-analyze email to refresh AI analysis
  const handleReanalyze = useCallback(async () => {
    if (!email?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/inbox/triage/email/${email.id}/reanalyze`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(t('triagePanel.reanalyze') + ' ✓');
        // Update triage data with new analysis
        setTriageData(prev => ({
          ...prev,
          ai_brief: response.data.ai_brief
        }));
      }
    } catch (err) {
      console.error('Error re-analyzing email:', err);
      toast.error('Could not re-analyze email');
    } finally {
      setLoading(false);
    }
  }, [email, t]);
  
  useEffect(() => {
    if (email?.id && isOpen) {
      if (triageData?.message_id !== email.id) {
        setTriageData(null);
      }
      fetchTriageData();
    }
  }, [email?.id, isOpen, fetchTriageData, triageData?.message_id]);
  
  // Transform legacy analysis
  const transformLegacyAnalysis = (analysis) => ({
    category: analysis.category || 'Ukjent',
    priority: analysis.priority || 'NORMAL',
    risk_level: analysis.risk_level || 'MEDIUM',
    what_this_is: analysis.what_this_is || analysis.summary || null,
    known_facts: analysis.what_we_know || analysis.key_facts || [],
    missing_info: analysis.missing_docs || analysis.must_clarify || [],
    risks_and_deadlines: analysis.risks_and_deadlines || null,
    deadline_detected: analysis.deadline_detected,
    suggested_price: analysis.suggested_price,
    work_estimate: analysis.work_estimate
  });
  
  // === ACTION HANDLERS ===
  
  // Opens the create case modal for review
  const handleCreateCaseAndStart = () => {
    setShowCreateCaseModal(true);
  };
  
  // Actually creates the case after user confirms in modal
  const handleConfirmCreateCase = async (formData) => {
    setActionInProgress('create');
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/inbox/triage/email/${email.id}/create-case-and-open`,
        {
          // Case details
          title: formData.title,
          case_type: formData.case_type,
          description: formData.description,
          priority: formData.priority,
          tags: formData.tags,
          // Client handling - either existing or new
          client_id: formData.client_id || null,
          new_client: formData.new_client || null,
          // Legacy fields for backwards compatibility
          client_name: formData.client_name || formData.new_client?.person_name || formData.new_client?.company_name,
          client_org: formData.client_org || formData.new_client?.company_name,
          client_email: formData.client_email || formData.new_client?.email || formData.new_client?.contacts?.[0]?.email
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      // Build success message
      let successMsg = `Sak "${formData.title}" opprettet!`;
      if (result.client) {
        successMsg = `Sak og klient "${result.client.name}" opprettet!`;
      }
      toast.success(successMsg);
      
      setShowCreateCaseModal(false);
      
      // Stay in inbox - just refresh triage data instead of navigating
      // User requested: "bli værende i innboks, ikke redirected"
      fetchTriageData();
      
      // Only open Word if there's a document
      if (result.word_addin_url) {
        toast.info('Document available in Word Add-in', { duration: 3000 });
      }
      
      // Notify parent that case was assigned (for email list update)
      if (result.case?.id) {
        onAssignCase?.(result.case.id);
        // Don't navigate - stay in inbox
        // onNavigateToCase?.(result.case.id);  // Removed per user request
      }
      
    } catch (err) {
      console.error('Error creating case:', err);
      toast.error('Could not create case');
    } finally {
      setActionInProgress(null);
    }
  };
  
  const handleLinkToCase = async (caseId) => {
    setActionInProgress('link');
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/inbox/triage/email/${email.id}/assign-case`,
        { case_id: caseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Email linked to case');
      setShowCaseSelector(false);
      onAssignCase?.(caseId);
      fetchTriageData();
    } catch (err) {
      toast.error('Could not link to case');
    } finally {
      setActionInProgress(null);
    }
  };
  
  const handleOpenPreCase = async () => {
    setActionInProgress('precase');
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/inbox/triage/email/${email.id}/create-precase`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Intake draft created');
      
      if (response.data.precase_id) {
        onNavigateToCase?.(`precase/${response.data.precase_id}`);
      }
      
      fetchTriageData();
    } catch (err) {
      toast.info('Creating as draft case...');
      handleCreateCaseAndStart();
    } finally {
      setActionInProgress(null);
    }
  };
  
  const handleGenerateReply = async () => {
    setActionInProgress('generate-reply');
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/inbox/triage/email/${email.id}/quick-reply`,
        { message_id: email.id, tone: 'professional' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplyDraft(response.data.draft);
    } catch (err) {
      toast.error('Could not generate reply');
    } finally {
      setActionInProgress(null);
    }
  };
  
  const handleSendReply = async () => {
    if (!replyDraft.trim()) {
      handleGenerateReply();
      setReplyOpen(true);
      return;
    }
    
    setActionInProgress('send-reply');
    try {
      const token = auth.getToken();
      const messageId = email.outlook_message_id || email.id;
      
      await axios.post(
        `${API}/outlook/reply`,
        { message_id: messageId, body: replyDraft, is_html: false, reply_all: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await axios.post(
        `${API}/inbox/triage/email/${email.id}/set-status`,
        { status: 'AWAITING_CUSTOMER' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Reply sent! Email set to "Awaiting customer"');
      setReplyOpen(false);
      setReplyDraft('');
      fetchTriageData();
    } catch (err) {
      toast.error('Could not send reply');
    } finally {
      setActionInProgress(null);
    }
  };
  
  const handleArchive = async () => {
    setActionInProgress('archive');
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/inbox/triage/email/${email.id}/archive`,
        { reason: archiveReason || 'Not relevant' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('E-post arkivert');
      setShowArchiveConfirm(false);
      onClose?.();
    } catch (err) {
      toast.error('Kunne ikke arkivere e-post');
    } finally {
      setActionInProgress(null);
    }
  };
  
  // Quick dismiss for irrelevant emails
  const handleQuickDismiss = async (reason: string) => {
    setActionInProgress('dismiss');
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/inbox/triage/email/${email.id}/archive`,
        { reason: reason, dismissed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('E-post ignorert');
      onClose?.();
    } catch (err) {
      toast.error('Kunne ikke ignorere e-post');
    } finally {
      setActionInProgress(null);
    }
  };
  
  // Mark for read later
  const handleReadLater = async () => {
    setActionInProgress('read-later');
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/inbox/triage/email/${email.id}/set-status`,
        { status: 'READ_LATER' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Merket for lesing senere');
      onClose?.();
    } catch (err) {
      toast.error('Kunne ikke merke e-post');
    } finally {
      setActionInProgress(null);
    }
  };
  
  // Create auto-archive rule for sender
  const handleCreateAutoRule = async (senderEmail: string) => {
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/inbox/rules/create`,
        { 
          type: 'auto_archive',
          sender_email: senderEmail,
          action: 'archive'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Also archive the current email
      await handleQuickDismiss('auto_rule_created');
    } catch (err) {
      console.error('Could not create auto-rule:', err);
      // Still dismiss the email even if rule creation fails
      await handleQuickDismiss('auto_rule_failed');
    }
  };
  
  // Handle attachment open
  const handleOpenAttachment = async (attachment: any) => {
    if (!attachment) return;
    
    try {
      // If attachment has a URL, open it
      if (attachment.contentUrl || attachment.url) {
        window.open(attachment.contentUrl || attachment.url, '_blank');
        return;
      }
      
      // Get attachment ID from various possible fields
      const attachmentId = attachment.id || attachment.outlook_attachment_id || attachment.attachmentId;
      
      // If attachment has an ID, try to download via API with authorization
      if (attachmentId && email?.outlook_message_id) {
        const token = auth.getToken();
        const downloadUrl = `${API}/outlook/messages/${email.outlook_message_id}/attachments/${attachmentId}/download`;
        
        // Fetch the attachment with authorization header
        const response = await fetch(downloadUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error('Failed to download attachment');
        }
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', attachment.name || 'attachment');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }
      
      // If no valid ID, show info message
      if (!attachmentId) {
        toast.info(`${attachment.name || 'Vedlegg'} - forhåndsvisning ikke tilgjengelig`);
        return;
      }
      
      toast.info(`Attachment: ${attachment.name || 'Unknown'}`);
    } catch (error) {
      console.error('Error opening attachment:', error);
      toast.error('Kunne ikke åpne vedlegg');
    }
  };
  
  if (!isOpen || !email) return null;
  
  const brief = triageData?.ai_brief;
  const suggestedCase = triageData?.suggested_case;
  const attachments = triageData?.attachments || email?.attachments || [];
  const hasAttachments = attachments.length > 0;
  const isAssigned = triageData?.status === 'ASSIGNED';
  const matchScore = suggestedCase?.confidence || 0;
  const isHighMatch = matchScore >= 0.7;
  
  return (
    <div 
      className="h-full flex flex-col bg-[#F9F9F8]"
      style={{ width }}
    >
      {/* Beautiful Gradient Header with Lexy AI inside */}
      <div className="flex-shrink-0 bg-gradient-to-b from-purple-50/80 via-[#F9F9F8] to-[#F9F9F8] pt-5 pb-4 px-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Lexy AI</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                <span className="text-[10px] text-gray-500 font-medium">
                  {loading ? 'Analyzing...' : 'Ready'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
      </div>
      
      {/* Scrollable Content Area - Off-white background */}
      <div className="flex-1 overflow-y-auto pt-3 pb-4 bg-[#F9F9F8]">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3 px-5">
            <div className="relative">
              <div className="w-5 h-5 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
            </div>
            <p className="text-xs text-gray-400">{t('inbox:triagePanel.analyzing')}</p>
          </div>
        ) : error && !triageData ? (
          <div className="py-10 text-center px-5">
            <AlertTriangle className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-xs text-gray-500 mb-3">{error}</p>
            <button 
              onClick={fetchTriageData} 
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-white/50 transition-all"
            >
              {t('inbox:triagePanel.tryAgain')}
            </button>
          </div>
        ) : (
          <div className="space-y-0 pb-20">
            {/* AI Brief - no extra padding, handled inside */}
            <div>
              <AIBriefSection 
                brief={brief}
                attachments={attachments}
                onCreateCase={handleCreateCaseAndStart}
                onOpenFullAnalysis={() => setShowFullAnalysis(true)}
                onOpenAttachment={handleOpenAttachment}
                onReanalyze={handleReanalyze}
                onDismiss={handleQuickDismiss}
                onArchive={() => handleArchive()}
                onReadLater={handleReadLater}
                onAutoRule={handleCreateAutoRule}
                actionInProgress={actionInProgress}
                isReanalyzing={loading}
                darkMode={false}
                senderEmail={email?.from?.emailAddress?.address || email?.from?.email || email?.sender}
              />
            </div>
            
            {/* Suggested case match */}
            {!isAssigned && isHighMatch && suggestedCase && (
              <div className="px-5">
              <div className="pt-4 border-t border-gray-200/50">
                <p className="text-[10px] text-gray-400 font-medium mb-2 uppercase tracking-wider">{t('inbox:triagePanel.existingCase')}</p>
                <p className="text-sm font-medium text-gray-900 mb-3">{suggestedCase.case_title}</p>
                <button
                  onClick={() => handleLinkToCase(suggestedCase.case_id)}
                  disabled={actionInProgress === 'link'}
                  className="w-full h-9 text-xs font-medium text-gray-600 hover:text-gray-900 bg-white/60 hover:bg-white/80 rounded-xl transition-all flex items-center justify-center gap-2 border border-gray-200/50"
                >
                  {actionInProgress === 'link' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {t('inbox:triagePanel.linkToThisCase')}
                </button>
              </div>
              </div>
            )}
            
            {/* Assigned State */}
            {isAssigned && (
              <div className="px-5">
              <div className="pt-4 border-t border-gray-200/50">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{t('inbox:triagePanel.assignedToCase')}</span>
                </div>
                <p className="text-sm text-gray-500 mb-3 ml-7">{triageData?.current_case_title}</p>
                <button 
                  className="w-full h-9 text-xs font-medium text-gray-600 hover:text-gray-900 bg-white/60 hover:bg-white/80 rounded-xl transition-all flex items-center justify-center gap-2 border border-gray-200/50"
                  onClick={() => onNavigateToCase?.(triageData?.current_case_id)}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {t('inbox:triagePanel.openCase')}
                </button>
              </div>
              </div>
            )}
            
            {/* Reply Panel */}
            <AnimatePresence>
              <ReplyPanel
                isOpen={replyOpen}
                onClose={() => setReplyOpen(false)}
                replyDraft={replyDraft}
                onReplyChange={setReplyDraft}
                onGenerateReply={handleGenerateReply}
                onSendReply={handleSendReply}
                actionInProgress={actionInProgress}
                darkMode={false}
              />
            </AnimatePresence>
          </div>
        )}
      </div>
      
      {/* Footer Actions - Minimal */}
      {!loading && triageData && !isAssigned && (
        <div className="flex-shrink-0 px-4 py-3 bg-gradient-to-t from-gray-100/80 to-transparent">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setShowCaseSelector(true)}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              {t('inbox:triagePanel.linkToCase')}
            </button>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <button
              onClick={() => setReplyOpen(true)}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              {t('inbox:triagePanel.replyAndWait')}
            </button>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <button
              onClick={() => setShowArchiveConfirm(true)}
              disabled={actionInProgress === 'archive'}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              {t('inbox:triagePanel.archive')}
            </button>
          </div>
        </div>
      )}
      
      {/* Modals */}
      <CaseSelectorModal
        isOpen={showCaseSelector}
        onClose={() => setShowCaseSelector(false)}
        cases={cases}
        onSelectCase={handleLinkToCase}
        actionInProgress={actionInProgress}
        suggestedCaseId={suggestedCase?.case_id}
        onCreateNew={() => {
          setShowCaseSelector(false);
          setShowCreateCaseModal(true);
        }}
      />
      
      <ArchiveModal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        archiveReason={archiveReason}
        onReasonChange={setArchiveReason}
        onConfirm={handleArchive}
        actionInProgress={actionInProgress}
      />
      
      {/* Create Case Modal - Review before creation with client management */}
      <CreateCaseModal
        isOpen={showCreateCaseModal}
        onClose={() => setShowCreateCaseModal(false)}
        prefillData={triageData?.new_case_prefill}
        brief={brief}
        attachments={attachments}
        onConfirm={handleConfirmCreateCase}
        isCreating={actionInProgress === 'create'}
        senderEmail={triageData?.new_case_prefill?.suggested_client_email || brief?.sender_email || email?.from?.email || ''}
        senderName={triageData?.new_case_prefill?.suggested_client_name || brief?.sender_name || email?.from?.name || ''}
        senderOrg={triageData?.new_case_prefill?.suggested_client_org || brief?.sender_org || ''}
      />
      
      {/* Full Analysis Modal - Deep dive work surface */}
      <FullAnalysisModal
        isOpen={showFullAnalysis}
        onClose={() => setShowFullAnalysis(false)}
        brief={brief}
        email={email}
        onGenerateReply={() => {
          setShowFullAnalysis(false);
          handleGenerateReply();
          setReplyOpen(true);
        }}
      />
    </div>
  );
};

export default InboxTriagePanelV2;
